import React, { useCallback, useEffect, useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";

interface OrderSearchParams {
  status?: string;
  customer?: string;
  type?: string;
}
interface OrderFiltersProps {
  initialOrderSearchParams: OrderSearchParams;
  onFilterChange: (params: OrderSearchParams) => void;
  loading?: boolean;
}

const OrderFilters = ({
  initialOrderSearchParams,
  onFilterChange,
  loading,
}: OrderFiltersProps) => {
  const [customerOptions, setCustomerOptions] = useState<FieldValue[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>(
    initialOrderSearchParams.status ?? "",
  );
  const [selectedCustomer, setSelectedCustomer] = useState<string>(
    initialOrderSearchParams.customer ?? "",
  );
  const [selectedType, setSelectedType] = useState<string>(
    initialOrderSearchParams.type ?? "",
  );

  const fetchCustomerOptions = async () => {
    const response = await api.get(
      API_URLS.ORDERS +
        "/_view/customers/distinct" +
        (selectedType ? `?type=${selectedType}` : "") +
        (selectedStatus ? `?status=${selectedStatus}` : ""),
    );
    setCustomerOptions((response.data as FieldValue[]) || []);
  };

  const setFilterValue = useCallback(
    (key: keyof OrderSearchParams, value: string) => {
      const newFilters: OrderSearchParams = {
        ...initialOrderSearchParams,
        [key]: value,
      };
      onFilterChange(newFilters);
      if (key === "status") {
        setSelectedStatus(value);
      } else if (key === "customer") {
        setSelectedCustomer(value);
      } else if (key === "type") {
        setSelectedType(value);
      }
    },
    [initialOrderSearchParams, onFilterChange],
  );

  useEffect(() => {
    fetchCustomerOptions();
  }, [selectedStatus, selectedType]);

  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Type Filter */}
        <div>
          <label
            htmlFor="selectedType"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Origem
          </label>
          <select
            id="selectedType"
            value={selectedType}
            onChange={(e) => setFilterValue("type", e.target.value)}
            disabled={loading}
            className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Todas as origens</option>
            <option value="DEFAULT">Revendedor</option>
            <option value="RESELLER">Comercial</option>
            <option value="END_CUSTOMER">Interna</option>
          </select>
        </div>

        {/* Customer Filter */}
        <div>
          <label
            htmlFor="customer"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Cliente
          </label>
          <select
            id="customer"
            value={selectedCustomer}
            onChange={(e) => setFilterValue("customer", e.target.value)}
            onFocus={fetchCustomerOptions}
            disabled={loading}
            className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Todos os clientes</option>
            {customerOptions.map((opt, idx) => (
              <option key={idx} value={opt.value?.toString()}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <label
            htmlFor="status"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Estado
          </label>
          <select
            id="status"
            value={selectedStatus}
            onChange={(e) => setFilterValue("status", e.target.value)}
            disabled={loading}
            className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Todos os estados</option>
            <option value="PENDING">Pendente</option>
            <option value="CONFIRMED">Confirmada</option>
            <option value="REJECTED">Rejeitada</option>
            <option value="SHIPPED">Enviada</option>
            <option value="COMPLETED">Concluída</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default OrderFilters;
